import React, { useState, useEffect, useRef } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import PromptEmailEditor from '../components/PromptEmailEditor';
import BASE_URL from '../utils/api';

// Helper: listens for MobileFooter generic + event
function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

function RelacionamentoClientesDropdown() {
    const [relacionamentos, setRelacionamentos] = useState([]);
    const [novoRelacionamentoAberto, setNovoRelacionamentoAberto] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dadosEdicao, setDadosEdicao] = useState(null);
    const [modoEdicao, setModoEdicao] = useState(false);

    // Estados para carregar dados relacionados
    const [clientes, setClientes] = useState([]);

    // Seleção de clientes em massa
    const [clientesSelecionados, setClientesSelecionados] = useState([]);
    const [popupClientesMassaAberto, setPopupClientesMassaAberto] = useState(false);
    const [filtroClienteMassa, setFiltroClienteMassa] = useState('');
    const [erroClienteDuplicado, setErroClienteDuplicado] = useState('');
    // Progresso de envio em massa
    const [enviandoMassa, setEnviandoMassa] = useState(false);
    // Resultado do último envio em massa: { assunto, resultados: [{ nome, email, enviado, erro }] }
    const [resultadoEnvio, setResultadoEnvio] = useState(null);
    const [progressoAtual, setProgressoAtual] = useState(0);
    const [progressoTotal, setProgressoTotal] = useState(0);

    // Templates de Prompt Email
    const [promptTemplates, setPromptTemplates] = useState([]);
    const [templateSelecionadoId, setTemplateSelecionadoId] = useState('');

    // Prompt Email Editor
    const [promptEmailAberto, setPromptEmailAberto] = useState(false);
    const [anexosSelecionados, setAnexosSelecionados] = useState([]);
    const [uploadingArquivos, setUploadingArquivos] = useState(false);
    const arquivoInputRef = useRef(null);

    // Filtros
    const [filtro, setFiltro] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('idRelacionamento');
    const [dropdownAberto, setDropdownAberto] = useState(false);

    const opcoesFiltro = [
        { label: 'ID Relacionamento', value: 'idRelacionamento' },
        { label: 'Cliente', value: 'cliente' },
        { label: 'Tipo de Evento', value: 'tipoEvento' },
        { label: 'Status', value: 'status' },
        { label: 'Data', value: 'data' }
    ];

    // Tipos de eventos de relacionamento
    const tiposEvento = [
        'Visita Pós-Venda',
        'Ligação de Follow-up',
        'Email de Satisfação',
        'Pesquisa de Feedback',
        'Reunião de Acompanhamento',
        'Atendimento de Dúvidas',
        'Suporte Técnico',
        'Treinamento',
        'Manutenção Preventiva',
        'Renovação de Contrato',
        'Upsell/Cross-sell',
        'Evento Corporativo',
        'Aniversário da Empresa',
        'Datas Comemorativas',
        'Newsletter',
        'Outro'
    ];

    // Status de relacionamento
    const statusOpcoes = [
        'Agendado',
        'Em Andamento',
        'Concluído',
        'Cancelado',
        'Reagendado',
        'Aguardando Retorno'
    ];

    // URLs do backend com fallback
    const BASE_URLS = [BASE_URL];

    const BASE_URLS_CLIENTE = [
        `${BASE_URL}/Cliente`,
        `${BASE_URL}/Cliente`,
        '/api/Cliente'
    ];

    // Função para fazer chamadas à API com fallback
    const apiCall = async (urls, endpoint = '', method = 'GET', data = null) => {
        let lastError;
        for (const baseUrl of urls) {
            try {
                const url = endpoint ? `${baseUrl}/${endpoint}` : baseUrl;
                console.log(`Tentando ${method} ${url}`);
                const config = {
                    method,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                };
                const token = getAuthToken();
                if (token) config.headers['Authorization'] = `Bearer ${token}`;
                if (data && method !== 'GET') {
                    config.body = JSON.stringify(data);
                }
                const response = await fetch(url, config);
                if (!response.ok) {
                    // O servidor respondeu com erro: não tenta de novo (senão criaria/enviaria duas vezes)
                    const texto = await response.text().catch(() => '');
                    let mensagem = `Erro ${response.status}: ${response.statusText}`;
                    try { const j = JSON.parse(texto); mensagem = j.erroEmail || j.message || j.mensagem || mensagem; } catch { if (texto) mensagem = texto; }
                    const erro = new Error(mensagem);
                    erro.respostaDoServidor = true;
                    throw erro;
                }
                const result = await response.json();
                console.log(`✅ Sucesso ${method} ${url}`);
                return result;
            } catch (error) {
                console.log(`❌ Erro ${method} ${baseUrl}${endpoint ? '/' + endpoint : ''}:`, error.message);
                if (error.respostaDoServidor) throw error;
                lastError = error;
                continue;
            }
        }
        throw lastError;
    };

    // URLs de PromptEmail
    const BASE_URLS_PROMPT = [
        `${BASE_URL}/PromptEmail`,
        `${BASE_URL}/PromptEmail`,
        '/api/PromptEmail'
    ];

    const apiUploadMultipart = async (endpoint, formData) => {
        const bases = [
            BASE_URL.replace(/\/api\/?$/, ''),
            BASE_URL.replace(/\/api\/?$/, ''),
            ''
        ];
        let lastError;
        for (const base of bases) {
            const url = base ? `${base}/api/${endpoint}` : `/api/${endpoint}`;
            try {
                const headers = {};
                const token = getAuthToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const response = await fetch(url, {
                    method: 'POST',
                    body: formData,
                    headers
                });
                const text = await response.text();
                if (!response.ok) {
                    // O servidor respondeu com erro: não tenta de novo (senão o e-mail poderia sair duas vezes)
                    let mensagem = `${response.status} ${response.statusText}`;
                    try { const j = JSON.parse(text); mensagem = j.erroEmail || j.message || j.mensagem || mensagem; } catch { if (text) mensagem = text; }
                    const erro = new Error(mensagem);
                    erro.respostaDoServidor = true;
                    throw erro;
                }
                try { return JSON.parse(text); } catch { return text; }
            } catch (error) {
                if (error.respostaDoServidor) throw error;
                lastError = error;
                continue;
            }
        }
        throw lastError || new Error('Falha ao enviar arquivos');
    };

    const handleOpenAnexos = () => {
        arquivoInputRef.current?.click();
    };

    const cleanupUploadedAnexos = () => {
        anexosSelecionados.forEach(a => {
            if (a.previewUrl) {
                URL.revokeObjectURL(a.previewUrl);
            }
        });
        setAnexosSelecionados([]);
    };

    const handleRemoveAnexo = (key) => {
        setAnexosSelecionados(prev => {
            const removido = prev.find(a => a.key === key);
            if (removido?.previewUrl) {
                URL.revokeObjectURL(removido.previewUrl);
            }
            return prev.filter(a => a.key !== key);
        });
    };

    const handleAnexosSelecionados = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        const novos = files.map((file, index) => ({
            key: `${Date.now()}_${index}_${file.name}`,
            file,
            name: file.name,
            type: file.type,
            status: 'Pronto',
            error: null,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        }));

        setAnexosSelecionados(prev => [...prev, ...novos]);
        event.target.value = '';
    };

    const fetchPromptTemplates = async () => {
        try {
            const data = await apiCall(BASE_URLS_PROMPT);
            setPromptTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('❌ Erro ao carregar templates:', error);
            setPromptTemplates([]);
        }
    };

    // Carregar dados ao montar o componente
    useEffect(() => {
        fetchRelacionamentos();
        fetchClientes();
        fetchPromptTemplates();
    }, []);

    useEffect(() => {
        const handler = () => {
            setDropdownAberto(false);
            setNovoRelacionamentoAberto(false);
            setModoEdicao(false);
            setDadosEdicao(null);
        };
        window.addEventListener('relacionamento-clientes-fechar-todos-popups', handler);
        return () => window.removeEventListener('relacionamento-clientes-fechar-todos-popups', handler);
    }, []);

    const fetchRelacionamentos = async () => {
        try {
            setLoading(true);
            console.log('🤝 Carregando relacionamentos...');
            const data = await apiCall(BASE_URLS);
            setRelacionamentos(Array.isArray(data) ? data : []);
            console.log('✅ Relacionamentos carregados:', data);
        } catch (error) {
            console.error('❌ Erro ao carregar relacionamentos:', error);
            setRelacionamentos([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchClientes = async () => {
        try {
            const data = await apiCall(BASE_URLS_CLIENTE);
            setClientes(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('❌ Erro ao carregar clientes:', error);
            setClientes([]);
        }
    };

    // Buscar relacionamento por ID para edição
    const fetchRelacionamentoPorId = async (id) => {
        try {
            console.log('🔍 Buscando relacionamento por ID:', id);
            const relacionamento = await apiCall(BASE_URLS, `${id}`);
            setDadosEdicao(relacionamento);
            setModoEdicao(true);
            setNovoRelacionamentoAberto(true);
            console.log('✅ Relacionamento encontrado:', relacionamento);
        } catch (error) {
            console.error('❌ Erro ao buscar relacionamento:', error);
            alert('Erro ao carregar dados do relacionamento para edição.');
        }
    };

    // Filtrar relacionamentos
    const relacionamentosFiltrados = relacionamentos.filter(rel => {
        if (!filtro) return true;

        const valorFiltro = filtro.toLowerCase();

        switch (tipoFiltro) {
            case 'idRelacionamento':
                return String(rel.relacionamentoId || rel.RelacionamentoId || rel.id || rel.Id).toLowerCase().includes(valorFiltro);
            case 'cliente':
                return (rel.clienteNome || rel.ClienteNome || '').toLowerCase().includes(valorFiltro);
            case 'tipoEvento':
                return (rel.tipoEvento || rel.TipoEvento || '').toLowerCase().includes(valorFiltro);
            case 'status':
                return (rel.status || rel.Status || '').toLowerCase().includes(valorFiltro);
            case 'data':
                return (rel.dataEvento || rel.DataEvento || '').toLowerCase().includes(valorFiltro);
            default:
                return true;
        }
    });

    // Obter nome do cliente
    const getNomeCliente = (clienteId) => {
        const cliente = clientes.find(c => (c.id || c.Id) === clienteId);
        return cliente ? (cliente.nome || cliente.Nome) : `ID: ${clienteId}`;
    };

    // Função para obter cor do status
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'agendado':
                return '#2196f3'; // Azul
            case 'em andamento':
                return 'rgb(235, 133, 0)'; // Laranja
            case 'concluído':
                return '#4caf50'; // Verde
            case 'cancelado':
                return '#757575'; // Cinza
            case 'reagendado':
                return '#9c27b0'; // Roxo
            case 'aguardando retorno':
                return '#ff5722'; // Laranja avermelhado
            default:
                return '#666';
        }
    };

    // Função para obter ícone do tipo de evento
    const getEventoIcon = (tipoEvento) => {
        const tipo = tipoEvento?.toLowerCase() || '';
        if (tipo.includes('visita')) return '🏢';
        if (tipo.includes('ligação') || tipo.includes('telefone')) return '📞';
        if (tipo.includes('email')) return '📧';
        if (tipo.includes('pesquisa') || tipo.includes('feedback')) return '📊';
        if (tipo.includes('reunião')) return '👥';
        if (tipo.includes('suporte') || tipo.includes('atendimento')) return '🛠️';
        if (tipo.includes('treinamento')) return '📚';
        if (tipo.includes('manutenção')) return '🔧';
        if (tipo.includes('contrato') || tipo.includes('renovação')) return '📄';
        if (tipo.includes('venda') || tipo.includes('upsell') || tipo.includes('cross-sell')) return '💰';
        if (tipo.includes('evento')) return '🎉';
        if (tipo.includes('aniversário')) return '🎂';
        if (tipo.includes('newsletter')) return '📰';
        return '📋';
    };

    // Função para substituir botões de dados cliente pelo texto real do cliente
    const substituirDadosCliente = (html, cliente) => {
        if (!html || !cliente) return html;
        const endereco = cliente.endereco || cliente.Endereco || '';
        const numero = cliente.numero || cliente.Numero || '';
        const cidade = cliente.cidade || cliente.Cidade || '';
        const uf = cliente.uf || cliente.UF || '';
        const enderecoCompleto = `${endereco} - ${numero}, ${cidade} - ${uf}`;

        const mapa = {
            'cliente_nome': cliente.nome || cliente.Nome || '',
            'cliente_email': cliente.email || cliente.Email || '',
            'cliente_celular': cliente.celular || cliente.Celular || '',
            'cliente_cpf_cnpj': cliente.cpF_CNPJ || cliente.CPF_CNPJ || cliente.CpF_CNPJ || '',
            'cliente_endereco': enderecoCompleto,
            'cliente_cidade': cidade,
            'cliente_estado': uf,
            'cliente_cep': cliente.cep || cliente.CEP || '',
        };

        let resultado = html;
        // Substituir <button class="dados-btn" data-dados="key">label</button> por texto
        Object.entries(mapa).forEach(([key, valor]) => {
            const regex = new RegExp(`<button[^>]*data-dados="${key}"[^>]*>[^<]*<\/button>`, 'gi');
            resultado = resultado.replace(regex, valor);
        });
        return resultado;
    };

    // Formulário para criar/editar relacionamento
    const handleSubmitRelacionamento = async (e) => {
        e.preventDefault();

        const selectedTemplateId = templateSelecionadoId ? parseInt(templateSelecionadoId, 10) : null;
        const selectedTemplate = selectedTemplateId ? promptTemplates.find(t => (t.id || t.Id) === selectedTemplateId) : null;
        const tipoEvento = selectedTemplate ? (selectedTemplate.titulo || selectedTemplate.Titulo || '') : '';

        try {
            setLoading(true);

            if (modoEdicao && dadosEdicao) {
                // Editar relacionamento existente
                const clienteId = dadosEdicao?.clienteId || dadosEdicao?.ClienteId;
                const relacionamentoId = dadosEdicao.relacionamentoId || dadosEdicao.RelacionamentoId || dadosEdicao.id || dadosEdicao.Id;
                const relacionamentoData = {
                    id: relacionamentoId,
                    clienteId: clienteId,
                    tipoEvento: tipoEvento,
                    PromptEmailId: selectedTemplateId || null,
                    dataEvento: dadosEdicao?.dataEvento || dadosEdicao?.DataEvento || null,
                    status: dadosEdicao?.status || dadosEdicao?.Status || 'Agendado'
                };
                console.log('📝 Editando relacionamento:', relacionamentoId, relacionamentoData);
                await apiCall(BASE_URLS, `${relacionamentoId}`, 'PUT', relacionamentoData);
                alert('✅ Relacionamento editado com sucesso!');
                setNovoRelacionamentoAberto(false);
                setModoEdicao(false);
                setDadosEdicao(null);
                setClientesSelecionados([]);
                setTemplateSelecionadoId('');
                await fetchRelacionamentos();
                return;
            }

            // === ENVIO EM MASSA ===
            if (clientesSelecionados.length === 0) {
                alert('Selecione pelo menos um cliente.');
                setLoading(false);
                return;
            }

            const total = clientesSelecionados.length;
            setProgressoTotal(total);
            setProgressoAtual(0);
            setLoading(false);
            setEnviandoMassa(true);

            // Buscar template HTML base uma vez (sem ele não há o que enviar: avisa e não segue)
            let templateHtmlBase = '';
            if (selectedTemplate && selectedTemplateId) {
                try {
                    const templateCompleto = await apiCall(BASE_URLS_PROMPT, `${selectedTemplateId}`);
                    templateHtmlBase = templateCompleto.templateHtml || templateCompleto.TemplateHtml || '';
                } catch (e) {
                    console.error('❌ [RELACIONAMENTO] Não foi possível carregar o template do e-mail:', e);
                }
                if (!templateHtmlBase) {
                    setEnviandoMassa(false);
                    alert('Não foi possível carregar o conteúdo do template de e-mail selecionado (ou ele está vazio). Nenhum e-mail foi enviado. Abra o template, confira o conteúdo e tente de novo.');
                    return;
                }
            }

            // Resultado de cada cliente: quem recebeu e quem não recebeu (com o motivo)
            const resultados = [];
            for (let i = 0; i < clientesSelecionados.length; i++) {
                const cliente = clientesSelecionados[i];
                const clienteId = parseInt(cliente.id || cliente.Id);
                const nomeCliente = cliente.nome || cliente.Nome || `Cliente #${clienteId}`;
                const emailCliente = (cliente.email || cliente.Email || '').trim();
                let htmlProcessado = '';
                const resultado = { clienteId, nome: nomeCliente, email: emailCliente, enviado: false, erro: null };

                const relacionamentoData = {
                    clienteId,
                    tipoEvento,
                    PromptEmailId: selectedTemplateId || null,
                    status: 'Agendado'
                };

                const postPayload = { relacionamento: relacionamentoData };
                if (selectedTemplate && emailCliente && templateHtmlBase) {
                    htmlProcessado = substituirDadosCliente(templateHtmlBase, cliente);
                    postPayload.EmailDestinatario = emailCliente;
                    postPayload.Assunto = tipoEvento;
                    postPayload.TemplateHtml = htmlProcessado;
                }

                try {
                    if (selectedTemplate && !emailCliente) {
                        // Sem e-mail: registra o relacionamento, mas avisa que o e-mail não saiu
                        await apiCall(BASE_URLS, '', 'POST', { relacionamento: relacionamentoData });
                        resultado.erro = 'Cliente sem e-mail cadastrado.';
                    } else if (anexosSelecionados.length > 0 && selectedTemplate) {
                        // Só cria o relacionamento (sem e-mail): o e-mail sai uma vez só, abaixo, com os anexos
                        await apiCall(BASE_URLS, '', 'POST', { relacionamento: relacionamentoData });
                        const formDataEmail = new FormData();
                        formDataEmail.append('EmailDestinatario', emailCliente);
                        formDataEmail.append('Assunto', tipoEvento);
                        formDataEmail.append('TemplateHtml', htmlProcessado);
                        anexosSelecionados.forEach(anexo => {
                            if (anexo.file) formDataEmail.append('Files', anexo.file);
                        });
                        try {
                            await apiUploadMultipart('Relacionamento_Clientes/enviar-email-com-anexos', formDataEmail);
                            resultado.enviado = true;
                        } catch (emailError) {
                            resultado.erro = emailError.message || 'Falha ao enviar o e-mail com anexos.';
                        }
                    } else {
                        const resposta = await apiCall(BASE_URLS, '', 'POST', postPayload);
                        if (!selectedTemplate) {
                            resultado.enviado = null; // sem template: só registra o relacionamento, não há e-mail
                        } else if (resposta?.emailEnviado ?? resposta?.EmailEnviado) {
                            resultado.enviado = true;
                        } else {
                            resultado.erro = resposta?.erroEmail || resposta?.ErroEmail || 'O servidor não conseguiu enviar o e-mail.';
                        }
                    }
                } catch (clienteError) {
                    resultado.erro = clienteError.message || 'Erro ao processar o cliente.';
                }

                if (resultado.enviado === false) console.error(`❌ [RELACIONAMENTO] E-mail NÃO enviado para ${nomeCliente} <${emailCliente || 'sem e-mail'}>: ${resultado.erro}`);
                resultados.push(resultado);
                setProgressoAtual(i + 1);
            }

            setEnviandoMassa(false);
            setNovoRelacionamentoAberto(false);
            setClientesSelecionados([]);
            setTemplateSelecionadoId('');
            setAnexosSelecionados([]);
            if (selectedTemplate) {
                // Mostra quem recebeu e quem não recebeu (com o motivo)
                setResultadoEnvio({ assunto: tipoEvento, resultados });
            } else {
                alert(`✅ Relacionamento registrado para ${total} cliente(s).`);
            }
            await fetchRelacionamentos();

        } catch (error) {
            setEnviandoMassa(false);
            console.error('❌ Erro ao salvar relacionamento:', error);
            alert('Erro ao salvar relacionamento: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-geral fundo-branco" style={{ minHeight: 'calc(100vh - var(--global-header-height, 64px))', background: '#f0f0f0', padding: 20 }}>
            <h2 style={{ textAlign: 'center', color: 'rgb(235, 133, 0)', marginBottom: 20 }}>🤝 Pós-Venda - Relacionamento com Clientes</h2>

            <MobileAddListener onOpen={() => { setModoEdicao(false); setDadosEdicao(null); setClientesSelecionados([]); setTemplateSelecionadoId(''); setAnexosSelecionados([]); setNovoRelacionamentoAberto(true); }} />

            {/* Botões Adicionar e Criar Prompt Email */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
                <button
                    onClick={() => {
                        setModoEdicao(false);
                        setDadosEdicao(null);
                        setClientesSelecionados([]);
                        setTemplateSelecionadoId('');
                        setAnexosSelecionados([]);
                        setNovoRelacionamentoAberto(true);
                    }}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: 'rgb(235, 133, 0)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 600,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'background 0.3s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
                >
                    + Adicionar Relacionamento
                </button>
                <button
                    onClick={() => setPromptEmailAberto(true)}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: 'rgb(235, 133, 0)',
                        color: '#fff',
                        border: '2px solid rgb(235, 133, 0)',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 600,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.3s'
                    }}
                >
                    ✉ Criar Prompt Email
                </button>
            </div>

            {/* Campo de filtro */}
            <div className="filtro-container">
                <label className="filtro-label">Filtrar por:</label>
                <div className="filtro-input-container">
                    <div className="filtro-input-wrapper">
                        <input
                            type="text"
                            className="filtro-input"
                            placeholder={`Filtrar por ${
                                opcoesFiltro.find(op => op.value === tipoFiltro)?.label || tipoFiltro
                            }`}
                            value={filtro}
                            onChange={e => setFiltro(e.target.value)}
                        />
                        {filtro && (
                            <button
                                className="filtro-clear-button"
                                onClick={() => setFiltro('')}
                                title="Limpar filtro"
                            >
                                ×
                            </button>
                        )}
                    </div>

                    <div className="filtro-custom-select">
                        <button
                            className="filtro-custom-botao"
                            onClick={() => setDropdownAberto(!dropdownAberto)}
                        >
                            {opcoesFiltro.find(op => op.value === tipoFiltro)?.label || tipoFiltro}
                            <span className="seta-para-baixo">▼</span>
                        </button>
                        {dropdownAberto && (
                            <div className="filtro-custom-opcoes">
                                {opcoesFiltro.map(op => (
                                    <div
                                        key={op.value}
                                        onClick={() => {
                                            setTipoFiltro(op.value);
                                            setDropdownAberto(false);
                                        }}
                                    >
                                        {op.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Estatísticas */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: 16, 
                marginBottom: 24,
                padding: '0 16px'
            }}>
                {['Agendado', 'Em Andamento', 'Concluído'].map(status => {
                    const count = relacionamentos.filter(r => 
                        (r.status || r.Status || '').toLowerCase() === status.toLowerCase()
                    ).length;
                    const color = getStatusColor(status);
                    
                    return (
                        <div 
                            key={status}
                            style={{
                                background: 'white',
                                borderRadius: 12,
                                padding: 20,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                border: `2px solid ${color}`,
                                textAlign: 'center'
                            }}
                        >
                            <div style={{ fontSize: 32, fontWeight: 'bold', color }}>{count}</div>
                            <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>{status}</div>
                        </div>
                    );
                })}
            </div>

            {/* Lista de Relacionamentos */}
            {loading ? (
                <div className="loading">⏳ Carregando relacionamentos...</div>
            ) : (
                <div className="lista-entregas">
                    <div className="entregas-header">
                        <span>Total de relacionamentos: {relacionamentosFiltrados.length}</span>
                    </div>
                    {relacionamentosFiltrados.length === 0 ? (
                        <div className="sem-entregas">
                            <p>🤝 Nenhum relacionamento encontrado</p>
                            <p>Clique em "+" para registrar o primeiro evento de relacionamento</p>
                        </div>
                    ) : (
                        <div className="relacionamento-cards-container" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            {relacionamentosFiltrados.map(rel => {
                                const relId = rel.relacionamentoId || rel.RelacionamentoId || rel.id || rel.Id;
                                const status = rel.status || rel.Status || 'Agendado';
                                const statusColor = getStatusColor(status);
                                const tipoEvento = rel.tipoEvento || rel.TipoEvento || '';
                                const eventoIcon = getEventoIcon(tipoEvento);

                                return (
                                    <div 
                                        key={relId}
                                        className="relacionamento-card"
                                        style={{
                                            background: 'white',
                                            borderRadius: 14,
                                            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                                            border: `2.5px solid ${statusColor}`,
                                            padding: 32,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            transition: 'transform 0.2s, box-shadow 0.2s'
                                        }}
                                        onMouseOver={e => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseOut={e => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.12)';
                                        }}
                                    >
                                        <div className="relacionamento-card-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                                            <div className="relacionamento-card-header" style={{ 
                                                fontSize: 18, 
                                                fontWeight: 'bold', 
                                                color: statusColor, 
                                                marginBottom: 8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8
                                            }}>
                                                {eventoIcon} {tipoEvento}
                                                <span style={{
                                                    background: statusColor,
                                                    color: 'white',
                                                    padding: '4px 12px',
                                                    borderRadius: 6,
                                                    fontSize: 13,
                                                    fontWeight: '600'
                                                }}>
                                                    {status}
                                                </span>
                                            </div>
                                            
                                            <div className="relacionamento-card-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
                                                <div className="relacionamento-field relacionamento-field-id">
                                                    <span className="relacionamento-field-label" style={{ color: '#666', fontWeight: '600' }}>🆔 ID:</span>{' '}
                                                    <span className="relacionamento-field-value">#{relId}</span>
                                                </div>

                                                <div className="relacionamento-field relacionamento-field-cliente">
                                                    <span className="relacionamento-field-label" style={{ color: '#666', fontWeight: '600' }}>👤 Cliente:</span>{' '}
                                                    <span className="relacionamento-field-value" style={{ color: '#4caf50', fontWeight: '500' }}>
                                                        {rel.clienteNome || rel.ClienteNome || getNomeCliente(rel.clienteId || rel.ClienteId)}
                                                    </span>
                                                </div>

                                                {(rel.dataEvento || rel.DataEvento) && (
                                                    <div className="relacionamento-field relacionamento-field-data">
                                                        <span className="relacionamento-field-label" style={{ color: '#666', fontWeight: '600' }}>📅 Data:</span>{' '}
                                                        <span className="relacionamento-field-value">{new Date(rel.dataEvento || rel.DataEvento).toLocaleDateString('pt-BR')}</span>
                                                    </div>
                                                )}

                                                {(rel.clienteEmail || rel.ClienteEmail) && (
                                                    <div className="relacionamento-field relacionamento-field-email">
                                                        <span className="relacionamento-field-label" style={{ color: '#666', fontWeight: '600' }}>📧 Email:</span>{' '}
                                                        <span className="relacionamento-field-value">{rel.clienteEmail || rel.ClienteEmail}</span>
                                                    </div>
                                                )}

                                                {(rel.clienteCelular || rel.ClienteCelular) && (
                                                    <div className="relacionamento-field relacionamento-field-celular">
                                                        <span className="relacionamento-field-label" style={{ color: '#666', fontWeight: '600' }}>📱 Celular:</span>{' '}
                                                        <span className="relacionamento-field-value">{rel.clienteCelular || rel.ClienteCelular}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="relacionamento-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
                                            <button 
                                                className="btn-editar-relacionamento"
                                                style={{ 
                                                    background: statusColor, 
                                                    color: '#fff', 
                                                    border: 'none', 
                                                    borderRadius: 7, 
                                                    padding: '10px 24px', 
                                                    fontWeight: 600, 
                                                    fontSize: 16, 
                                                    minWidth: 120, 
                                                    cursor: 'pointer',
                                                    transition: 'opacity 0.2s'
                                                }}
                                                onClick={() => fetchRelacionamentoPorId(relId)}
                                                onMouseOver={e => e.currentTarget.style.opacity = '0.85'}
                                                onMouseOut={e => e.currentTarget.style.opacity = '1'}
                                            >
                                                Editar
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Popup Selecionar Clientes em Massa */}
            {popupClientesMassaAberto && (
                <div className="modal-overlay" onClick={() => setPopupClientesMassaAberto(false)} style={{ zIndex: 10000 }}>
                    <div
                        className="modal-content"
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: 560, width: '92vw', padding: 0, borderRadius: 16, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 10001 }}
                    >
                        <div className="modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid #eee', flexShrink: 0 }}>
                            <h3 style={{ margin: 0 }}>Selecionar Clientes</h3>
                            <button className="modal-close" onClick={() => setPopupClientesMassaAberto(false)}>×</button>
                        </div>

                        {/* Busca */}
                        <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
                            <input
                                type="text"
                                placeholder="Buscar cliente por nome..."
                                value={filtroClienteMassa}
                                onChange={e => { setFiltroClienteMassa(e.target.value); setErroClienteDuplicado(''); }}
                                autoFocus
                                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: '1.5px solid #ccc', fontSize: 14, boxSizing: 'border-box' }}
                            />
                            {erroClienteDuplicado && (
                                <div style={{ color: '#e53935', fontSize: 13, marginTop: 6, fontWeight: 600 }}>
                                    ⚠️ {erroClienteDuplicado}
                                </div>
                            )}
                        </div>

                        {/* Container superior — lista de clientes disponíveis */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px', minHeight: 0 }}>
                            <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Clientes disponíveis
                            </div>
                            {clientes
                                .filter(cli => (cli.nome || cli.Nome || '').toLowerCase().includes(filtroClienteMassa.toLowerCase()))
                                .map(cli => {
                                    const id = cli.id || cli.Id;
                                    const nome = cli.nome || cli.Nome;
                                    const jaSelecionado = clientesSelecionados.some(c => (c.id || c.Id) === id);
                                    return (
                                        <div
                                            key={id}
                                            onClick={() => {
                                                if (jaSelecionado) {
                                                    setErroClienteDuplicado(`"${nome}" já foi adicionado.`);
                                                    setTimeout(() => setErroClienteDuplicado(''), 2500);
                                                    return;
                                                }
                                                setErroClienteDuplicado('');
                                                setClientesSelecionados(prev => [...prev, cli]);
                                            }}
                                            style={{
                                                padding: '10px 14px', borderBottom: '1px solid #f0f0f0',
                                                cursor: jaSelecionado ? 'default' : 'pointer', borderRadius: 6,
                                                transition: 'background 0.15s', fontSize: 14, fontWeight: 500,
                                                color: jaSelecionado ? '#bbb' : '#333',
                                                background: jaSelecionado ? '#fafafa' : 'transparent',
                                                display: 'flex', alignItems: 'center', gap: 8
                                            }}
                                            onMouseEnter={e => { if (!jaSelecionado) e.currentTarget.style.background = 'rgba(235,133,0,0.1)'; }}
                                            onMouseLeave={e => { if (!jaSelecionado) e.currentTarget.style.background = jaSelecionado ? '#fafafa' : 'transparent'; }}
                                        >
                                            <span style={{ color: '#aaa', fontSize: 12, minWidth: 34 }}>#{id}</span>
                                            <span style={{ flex: 1 }}>{nome}</span>
                                            {jaSelecionado && <span style={{ fontSize: 12, color: '#4caf50', fontWeight: 700 }}>✓</span>}
                                        </div>
                                    );
                                })
                            }
                            {clientes.filter(cli => (cli.nome || cli.Nome || '').toLowerCase().includes(filtroClienteMassa.toLowerCase())).length === 0 && (
                                <p style={{ textAlign: 'center', color: '#aaa', padding: 16 }}>Nenhum cliente encontrado.</p>
                            )}
                        </div>

                        {/* Linha divisória */}
                        <div style={{ borderTop: '2px solid #eee', margin: '0 20px', flexShrink: 0 }} />

                        {/* Container inferior — clientes selecionados */}
                        <div style={{ maxHeight: 200, overflowY: 'auto', padding: '8px 20px', flexShrink: 0, background: '#fafafa' }}>
                            <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Selecionados ({clientesSelecionados.length})
                            </div>
                            {clientesSelecionados.length === 0 ? (
                                <p style={{ fontSize: 13, color: '#bbb', textAlign: 'center', padding: '8px 0' }}>Nenhum cliente selecionado ainda.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {clientesSelecionados.map(cli => {
                                        const id = cli.id || cli.Id;
                                        const nome = cli.nome || cli.Nome;
                                        return (
                                            <div key={id} style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                background: '#fff', borderRadius: 8, padding: '8px 12px',
                                                border: '1px solid #e0e0e0', fontSize: 14
                                            }}>
                                                <span>
                                                    <span style={{ color: '#aaa', fontSize: 12, marginRight: 6 }}>#{id}</span>
                                                    {nome}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setClientesSelecionados(prev => prev.filter(c => (c.id || c.Id) !== id))}
                                                    style={{ border: 'none', background: 'transparent', color: '#e53935', cursor: 'pointer', fontSize: 20, fontWeight: 700, lineHeight: 1, padding: '0 4px' }}
                                                    title="Remover"
                                                >×</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Botões */}
                        <div style={{ padding: '12px 20px 16px', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid #eee', flexShrink: 0 }}>
                            <button
                                type="button"
                                onClick={() => setPopupClientesMassaAberto(false)}
                                style={{ padding: '10px 20px', borderRadius: 6, border: '1.5px solid #ccc', background: '#fff', fontSize: 14, cursor: 'pointer', fontWeight: 600, color: '#555' }}
                            >Cancelar</button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (clientesSelecionados.length === 0) {
                                        setErroClienteDuplicado('Selecione pelo menos um cliente.');
                                        return;
                                    }
                                    setPopupClientesMassaAberto(false);
                                }}
                                style={{ padding: '10px 22px', borderRadius: 6, border: 'none', background: 'rgb(235,133,0)', color: '#fff', fontSize: 14, cursor: 'pointer', fontWeight: 700 }}
                            >Confirmar ({clientesSelecionados.length})</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Novo/Editar Relacionamento */}
            {novoRelacionamentoAberto && (
                <div className="modal-overlay" onClick={() => {
                    cleanupUploadedAnexos();
                    setNovoRelacionamentoAberto(false);
                    setModoEdicao(false);
                    setDadosEdicao(null);
                    setClientesSelecionados([]);
                }}>
                    <div 
                        className="modal-content" 
                        onClick={e => e.stopPropagation()} 
                        style={{ 
                            maxWidth: 700, 
                            minWidth: 500, 
                            width: '90vw', 
                            padding: 32, 
                            borderRadius: 16,
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}
                    >
                        <div className="modal-header">
                            {!modoEdicao && (
                                <button
                                    onClick={() => {
                                        cleanupUploadedAnexos();
                                        setNovoRelacionamentoAberto(false);
                                        setClientesSelecionados([]);
                                        setTemplateSelecionadoId('');
                                    }}
                                    style={{
                                        background: 'none', border: '1.5px solid #aaa', color: '#555',
                                        borderRadius: 6, padding: '6px 14px', fontSize: 13,
                                        cursor: 'pointer', fontWeight: 600
                                    }}
                                >
                                    ← Voltar
                                </button>
                            )}
                            <h3>{modoEdicao ? 'Editar Relacionamento' : 'Novo Relacionamento'}</h3>
                            <button 
                                className="modal-close"
                                onClick={() => {
                                    cleanupUploadedAnexos();
                                    setNovoRelacionamentoAberto(false);
                                    setModoEdicao(false);
                                    setDadosEdicao(null);
                                    setClientesSelecionados([]);
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <form 
                            onSubmit={handleSubmitRelacionamento} 
                            className="entrega-form" 
                            style={{ 
                                padding: 24, 
                                background: '#fff', 
                                borderRadius: 12, 
                                boxShadow: '0 2px 12px rgba(0,0,0,0.10)' 
                            }}
                        >
                            <div className="form-grid" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {/* Clientes */}
                                <div className="form-group">
                                    {modoEdicao ? (
                                        <div style={{ padding: '10px 14px', borderRadius: 6, border: '1.5px solid #ccc', background: '#f5f5f5', fontSize: 14, color: '#333', fontWeight: 600 }}>
                                            {getNomeCliente(dadosEdicao?.clienteId || dadosEdicao?.ClienteId)}
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setPopupClientesMassaAberto(true)}
                                                    style={{
                                                        padding: '10px 24px', borderRadius: 8,
                                                        border: '1.5px solid rgb(235,133,0)',
                                                        background: 'rgb(235,133,0)', color: '#fff',
                                                        fontSize: 15, fontWeight: 700, cursor: 'pointer'
                                                    }}
                                                >
                                                    Escolher Clientes {clientesSelecionados.length > 0 ? `(${clientesSelecionados.length} selecionado${clientesSelecionados.length > 1 ? 's' : ''})` : ''}
                                                </button>
                                            </div>
                                            {clientesSelecionados.length > 0 && (
                                                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                    {clientesSelecionados.map(cli => {
                                                        const id = cli.id || cli.Id;
                                                        const nome = cli.nome || cli.Nome;
                                                        return (
                                                            <div key={id} style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                                                background: '#fff3e0', borderRadius: 20, padding: '5px 12px',
                                                                border: '1px solid rgb(235,133,0)', fontSize: 13, fontWeight: 600
                                                            }}>
                                                                <span>{nome}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setClientesSelecionados(prev => prev.filter(c => (c.id || c.Id) !== id))}
                                                                    style={{ border: 'none', background: 'transparent', color: '#e53935', cursor: 'pointer', fontSize: 16, fontWeight: 700, lineHeight: 1, padding: 0 }}
                                                                >×</button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Tipo de Evento (Template de Email) */}
                                <div className="form-group">
                                    <label style={{ fontWeight: 600, color: '#444', marginBottom: 6, fontSize: 14 }}>
                                        Tipo de Evento: <span style={{ color: '#f44336' }}>*</span>
                                    </label>
                                    <select
                                        name="tipoEvento"
                                        required
                                        value={modoEdicao ? undefined : templateSelecionadoId}
                                        defaultValue={modoEdicao ? (dadosEdicao?.tipoEvento || dadosEdicao?.TipoEvento || '') : undefined}
                                        onChange={!modoEdicao ? (e) => setTemplateSelecionadoId(e.target.value) : undefined}
                                        style={{ 
                                            borderRadius: 6, 
                                            border: '1.5px solid #ccc', 
                                            padding: 10, 
                                            fontSize: 14, 
                                            width: '100%',
                                            background: 'white'
                                        }}
                                    >
                                        <option value="">Selecione um template de email</option>
                                        {promptTemplates.map(t => {
                                            const tId = t.id || t.Id;
                                            const tTitulo = t.titulo || t.Titulo || 'Sem título';
                                            return (
                                                <option key={tId} value={tId}>
                                                    {tTitulo}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                                    <button
                                        type="button"
                                        onClick={handleOpenAnexos}
                                        disabled={uploadingArquivos}
                                        style={{
                                            background: 'rgb(235, 133, 0)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: 8,
                                            padding: '10px 18px',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            cursor: uploadingArquivos ? 'not-allowed' : 'pointer',
                                            opacity: uploadingArquivos ? 0.7 : 1
                                        }}
                                    >
                                        {uploadingArquivos ? 'Enviando anexos...' : 'Anexar Arquivos'}
                                    </button>
                                    <input
                                        ref={arquivoInputRef}
                                        type="file"
                                        multiple
                                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,application/zip"
                                        style={{ display: 'none' }}
                                        onChange={handleAnexosSelecionados}
                                    />
                                    {anexosSelecionados.length > 0 && (
                                        <div style={{ width: '100%', marginTop: 12, textAlign: 'left', display: 'grid', gap: 16 }}>
                                            {anexosSelecionados.some(a => a.type.startsWith('image/')) && (
                                                <div>
                                                    <div style={{ fontSize: 13, color: '#444', marginBottom: 8, fontWeight: 600 }}>
                                                        Imagens selecionadas:
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10 }}>
                                                        {anexosSelecionados.filter(a => a.type.startsWith('image/')).map(anexo => (
                                                            <div
                                                                key={anexo.key}
                                                                style={{
                                                                    position: 'relative',
                                                                    width: '100%',
                                                                    minHeight: 90,
                                                                    borderRadius: 12,
                                                                    overflow: 'hidden',
                                                                    border: '1px solid #ddd',
                                                                    background: '#fff',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <img
                                                                    src={anexo.previewUrl}
                                                                    alt={anexo.name}
                                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveAnexo(anexo.key)}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 6,
                                                                        right: 6,
                                                                        width: 26,
                                                                        height: 26,
                                                                        minWidth: 26,
                                                                        minHeight: 26,
                                                                        padding: 0,
                                                                        borderRadius: '50%',
                                                                        border: 'none',
                                                                        background: 'rgba(0,0,0,0.55)',
                                                                        color: 'white',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: 14,
                                                                        lineHeight: 1
                                                                    }}
                                                                    aria-label={`Remover imagem ${anexo.name}`}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {anexosSelecionados.some(a => !a.type.startsWith('image/')) && (
                                                <div>
                                                    <div style={{ fontSize: 13, color: '#444', marginBottom: 8, fontWeight: 600 }}>
                                                        Outros arquivos:
                                                    </div>
                                                    <div style={{ display: 'grid', gap: 10 }}>
                                                        {anexosSelecionados.filter(a => !a.type.startsWith('image/')).map(anexo => (
                                                            <div
                                                                key={anexo.key}
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    background: '#f9f9f9',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: 8,
                                                                    padding: '10px 12px'
                                                                }}
                                                            >
                                                                <span style={{ fontSize: 14, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {anexo.name}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveAnexo(anexo.key)}
                                                                    style={{
                                                                        border: 'none',
                                                                        background: 'transparent',
                                                                        color: '#e53935',
                                                                        cursor: 'pointer',
                                                                        fontSize: 16,
                                                                        fontWeight: 700
                                                                    }}
                                                                    aria-label={`Remover arquivo ${anexo.name}`}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                            </div>

                            <div className="form-actions" style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
                                <button
                                    type="submit"
                                    className="btn-salvar"
                                    disabled={loading}
                                    style={{
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        opacity: loading ? 0.6 : 1
                                    }}
                                >
                                    {loading ? 'Enviando...' : (modoEdicao ? 'Atualizar' : 'Enviar')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <LoadingOverlay isLoading={loading} message="Processando..." />

            {/* Modal de Progresso de Envio em Massa */}
            {/* Resultado do envio: quem recebeu e quem não recebeu (com o motivo) */}
            {resultadoEnvio && (() => {
                const enviados = resultadoEnvio.resultados.filter(r => r.enviado);
                const falhas = resultadoEnvio.resultados.filter(r => r.enviado === false);
                return (
                    <div onClick={() => setResultadoEnvio(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95100, padding: 16 }}>
                        <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 620, maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: 22, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Resultado do envio</h3>
                                <button onClick={() => setResultadoEnvio(null)} aria-label="Fechar" style={{ flexShrink: 0, background: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                                <div style={{ flex: '1 1 160px', padding: 10, borderRadius: 8, background: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }}>✅ {enviados.length} enviado(s)</div>
                                <div style={{ flex: '1 1 160px', padding: 10, borderRadius: 8, background: falhas.length ? '#fdecea' : '#f5f5f5', color: falhas.length ? '#c62828' : '#777', fontWeight: 700 }}>❌ {falhas.length} não enviado(s)</div>
                            </div>
                            {falhas.length > 0 && (
                                <>
                                    <h4 style={{ margin: '0 0 6px', color: '#c62828' }}>Não receberam o e-mail</h4>
                                    {falhas.map((r, i) => (
                                        <div key={i} style={{ padding: '8px 10px', border: '1px solid #f5c6c6', background: '#fff8f8', borderRadius: 6, marginBottom: 6, fontSize: 13 }}>
                                            <strong>{r.nome}</strong> <span style={{ color: '#777' }}>{r.email || '(sem e-mail)'}</span>
                                            <div style={{ color: '#c62828', marginTop: 2 }}>{r.erro}</div>
                                        </div>
                                    ))}
                                </>
                            )}
                            {enviados.length > 0 && (
                                <>
                                    <h4 style={{ margin: '12px 0 6px', color: '#2e7d32' }}>Receberam o e-mail</h4>
                                    {enviados.map((r, i) => (
                                        <div key={i} style={{ padding: '6px 10px', borderBottom: '1px solid #eee', fontSize: 13 }}>
                                            ✅ <strong>{r.nome}</strong> <span style={{ color: '#777' }}>{r.email}</span>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}

            {enviandoMassa && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 18, padding: '36px 44px',
                        minWidth: 320, maxWidth: 460, width: '90vw',
                        boxShadow: '0 8px 40px rgba(0,0,0,0.3)', textAlign: 'center'
                    }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#333', marginBottom: 6 }}>
                            📧 Enviando Templates...
                        </div>
                        <div style={{ fontSize: 36, fontWeight: 800, color: 'rgb(235,133,0)', marginBottom: 18, letterSpacing: 2 }}>
                            {progressoAtual}/{progressoTotal}
                        </div>
                        <div style={{ background: '#f0f0f0', borderRadius: 14, height: 20, overflow: 'hidden', marginBottom: 14, position: 'relative' }}>
                            <div style={{
                                background: 'linear-gradient(90deg, rgb(235,133,0), rgb(255,168,40))',
                                height: '100%',
                                width: `${progressoTotal > 0 ? Math.round((progressoAtual / progressoTotal) * 100) : 0}%`,
                                borderRadius: 14,
                                transition: 'width 0.4s ease'
                            }} />
                        </div>
                        <div style={{ fontSize: 13, color: '#888' }}>
                            {progressoAtual < progressoTotal
                                ? `Enviando para cliente ${progressoAtual + 1} de ${progressoTotal}...`
                                : 'Finalizando...'}
                        </div>
                    </div>
                </div>
            )}

            {/* Prompt Email Editor - Full Screen */}
            {promptEmailAberto && (
                <PromptEmailEditor
                    onClose={() => setPromptEmailAberto(false)}
                    onSave={() => {
                        // O PromptEmailEditor agora faz o POST/PUT diretamente
                        // Apenas fecha o editor após o save bem-sucedido
                    }}
                />
            )}
        </div>
    );
}

export default RelacionamentoClientesDropdown;
